/**
 * E2E renderer test probe (ENG-4321) — lets Playwright drive a SIMULATED
 * retry-then-succeed / retry-then-fail attempt sequence through the REAL
 * terminal-outcome controller + emit path via
 * `globalThis.__irisSimulateStreamRecovery`.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. The production
 * trigger is the LiveKit video stall/retry loop
 * (livekit/components/video/video.tsx), which needs a real stalled media stream
 * that e2e cannot deterministically produce. This probe feeds the SAME
 * controller (analytics/domains/terminal-outcome.ts) the SAME per-attempt
 * outcomes the loop feeds, so the terminal event reaches PostHog through the
 * SAME emit + envelope enrichment (analytics/domains/stream-recovery.ts) — the
 * receipt asserts the wire, not a test double.
 *
 * It drives the production singleton (streamRecoveryController), not a fresh
 * controller, so the per-camera stall episode that merges several views of one
 * camera is on the receipt's path as well.
 *
 * The gate mirrors calibration-probe.ts: `process.env` is NOT populated in this
 * renderer, so IRIS_E2E_TEST is read off `window.electron.process.env` (the
 * preload bridge), the same path posthog/manager.ts reads it through.
 */
import type { AttemptOutcome } from '#~/analytics/domains/terminal-outcome.js';
import type { Root } from 'react-dom/client';
import { LiveKitImpl } from '#~/livekit/livekit.js';
import { broker as agentBroker } from '#~/agent/broker.js';
import { toIdFromHex } from '#~/api/utils.js';
import {
  noteStreamProgress,
  streamRecoveryController
} from '#~/analytics/domains/stream-recovery.js';

/**
 * One step a video view of one camera takes during an outage, in the order
 * video.tsx takes them: a view opens its retry window when its stream stalls,
 * gives up when the window's budget runs out, recovers on its first good
 * attempt, or unmounts (abandons its window) mid-outage. `play` is real
 * playback (the `timeupdate` listener), which ends the outage.
 */
export type StreamOutageStep =
  | { op: 'open'; view: string }
  | { op: 'give-up'; view: string }
  | { op: 'recover'; view: string }
  | { op: 'unmount'; view: string }
  | { op: 'play' };

/** A small view budget so a give-up takes few attempts; merging ignores the budget. */
const SCRIPT_BUDGET = { maxAttempts: 3, windowMs: 120_000 };
/** Upper bound on failed attempts a give-up drives before the probe stops. */
const MAX_GIVE_UP_ATTEMPTS = 50;

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

type ViewerReattachObservation = {
  generation: number;
  localReadyByAgent: ReadonlyMap<string, number>;
};

// A completed startup reattach may precede a later test's preparation. Keep
// its passive history by the actual resource, never by camera/window identity.
const viewerReattachObservations = new WeakMap<LiveKitImpl, ViewerReattachObservation>();
let viewerReattachObserverInstalled = false;
let viewerReattachObserverOrdinal = 0;

export function readViewerReattachObservation(
  livekit: LiveKitImpl,
  agentId: string
): { generation: number; localReadyGeneration: number | undefined } {
  const observed = viewerReattachObservations.get(livekit);
  return {
    generation: observed?.generation ?? 0,
    localReadyGeneration: observed?.localReadyByAgent.get(agentId)
  };
}

/** Observe the real final reattach before React starts; never drive that chain. */
export function installViewerReattachObservation(): () => void {
  if (!isE2eEnabled() || viewerReattachObserverInstalled) return () => {};
  const original = LiveKitImpl.prototype.forceVideoReattach;
  const observed: typeof original = function (this: LiveKitImpl, ...args) {
    let observerKey: string;
    do {
      observerKey = `viewer-recovery-readiness-${++viewerReattachObserverOrdinal}`;
    } while (this.reattachListeners.has(observerKey));
    const record = (): void => {
      // This is the final real listener: an earlier throwing listener cannot
      // fabricate completion. Diagnostic sampling must never reject playback.
      try {
        const previous = viewerReattachObservations.get(this);
        const generation = (previous?.generation ?? 0) + 1;
        const localReadyByAgent = new Map(previous?.localReadyByAgent);
        for (const agent of agentBroker) {
          if (agent.roomId !== this.id) continue;
          const id = toIdFromHex(agent.id);
          if (id === undefined) continue;
          if (
            this.modeForAgent(id) === 'local' &&
            this.connectedModeForAgent(id) === 'local' &&
            this.isLocalRoomConnected(id)
          ) {
            localReadyByAgent.set(agent.id, generation);
          }
        }
        viewerReattachObservations.set(this, { generation, localReadyByAgent });
      } catch {
        // Readiness remains unknown if inspection fails; the real call and
        // its original Promise still carry their own success or rejection.
      }
    };
    this.reattachListeners.set(observerKey, record);
    try {
      return original.apply(this, args);
    } finally {
      if (this.reattachListeners.get(observerKey) === record)
        this.reattachListeners.delete(observerKey);
    }
  };
  LiveKitImpl.prototype.forceVideoReattach = observed;
  viewerReattachObserverInstalled = true;
  return () => {
    if (LiveKitImpl.prototype.forceVideoReattach === observed)
      LiveKitImpl.prototype.forceVideoReattach = original;
    viewerReattachObserverInstalled = false;
  };
}

/** The video loop's own budget (video.tsx MAX_RETRY_ATTEMPTS / MAX_RETRY_DURATION_MS). */
const HOST_BUDGET = { maxAttempts: 30, windowMs: 120_000 };

if (isE2eEnabled()) {
  installViewerReattachObservation();
  // Per e2e-first-tdd: actual Viewer replacement must retain the original
  // reporting window. These inputs never call the controller or timeupdate.
  Reflect.set(
    globalThis,
    '__irisPrepareViewerRecovery',
    async (key: string, roomId: string, agentId: string) => {
      const { prepareViewerRecovery } = await import('./viewer-recovery-probe.js');
      return prepareViewerRecovery(key, roomId, agentId);
    }
  );
  Reflect.set(
    globalThis,
    '__irisDriveViewerRecovery',
    async (key: string, op: 'state' | 'pause' | 'dispose') => {
      const { driveViewerRecovery } = await import('./viewer-recovery-probe.js');
      return driveViewerRecovery(key, op);
    }
  );
  // One paused canvas source per view. Video settles recovery only from presented
  // frames (or an advancing clock where frame callbacks are absent), so a bare
  // `timeupdate` no longer proves progress. A per-view source lets the receipt
  // advance exactly one view's playback while the other stays stalled.
  type VideoSource = { canvas: HTMLCanvasElement; media: CanvasCaptureMediaStreamTrack };
  type VideoReceipt = {
    container: HTMLDivElement;
    root: Root;
    sources: [VideoSource, VideoSource];
    readWindow: (element: HTMLVideoElement) => boolean | undefined;
  };
  const stopSources = (sources: VideoSource[]): void => {
    for (const { media } of sources) media.stop();
  };
  // Present real frames to one view: paint, request a frame, and wait for the
  // element to present it. Two frames give Video a baseline and then progress.
  const presentFrames = async (element: HTMLVideoElement, source: VideoSource): Promise<void> => {
    const context = source.canvas.getContext('2d');
    if (context === null) throw new Error('Video recovery receipt cannot paint its source');
    for (let frame = 0; frame < 3; frame++) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error('Video recovery receipt view never presented a frame')),
          5_000
        );
        element.requestVideoFrameCallback(() => {
          clearTimeout(timer);
          resolve();
        });
        context.fillStyle = frame % 2 === 0 ? '#000' : '#fff';
        context.fillRect(0, 0, source.canvas.width, source.canvas.height);
        source.media.requestFrame();
      });
    }
  };
  const videoReceipts = new Map<string, VideoReceipt>();
  Reflect.set(
    globalThis,
    '__irisPrepareVideoRecoveryViews',
    async (key: string, deviceId: string, agentId?: string): Promise<void> => {
      if (videoReceipts.has(key)) throw new Error('Video recovery receipt already owns this key');
      const [
        { createElement },
        { createRoot },
        { LocalVideoTrack },
        { Video, readVideoRecoveryWindow }
      ] = await Promise.all([
        import('react'),
        import('react-dom/client'),
        import('livekit-client'),
        import('#~/livekit/components/video/video.js')
      ]);
      const createSource = (): VideoSource => {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        // Zero fps: frames arrive only when the receipt requests them.
        const media = canvas.captureStream(0).getVideoTracks()[0];
        if (media === undefined)
          throw new Error('Video recovery receipt requires a local video track');
        return { canvas, media: media as CanvasCaptureMediaStreamTrack };
      };
      const sources: [VideoSource, VideoSource] = [createSource(), createSource()];
      const [trackA, trackB] = sources.map(({ media }) => new LocalVideoTrack(media));
      const container = document.createElement('div');
      document.body.appendChild(container);
      const root = createRoot(container);
      const mounted = new Promise<void>((resolve) => {
        const observer = new MutationObserver(() => {
          if (container.querySelectorAll('video').length === 2) {
            observer.disconnect();
            resolve();
          }
        });
        observer.observe(container, { childList: true, subtree: true });
      });
      root.render(
        createElement(
          'div',
          {},
          createElement(Video, {
            track: trackA,
            deviceId,
            ...(agentId !== undefined ? { agentId } : {}),
            isEmbedded: true,
            muted: true
          }),
          createElement(Video, {
            track: trackB,
            deviceId,
            ...(agentId !== undefined ? { agentId } : {}),
            isEmbedded: true,
            muted: true
          })
        )
      );
      await mounted;
      if (container.querySelectorAll('video').length !== 2) {
        root.unmount();
        container.remove();
        stopSources(sources);
        throw new Error('Video recovery receipt did not mount two production views');
      }
      videoReceipts.set(key, { container, root, sources, readWindow: readVideoRecoveryWindow });
    }
  );
  Reflect.set(
    globalThis,
    '__irisDriveVideoRecovery',
    async (
      key: string,
      op: 'state' | 'progress-a' | 'progress-b' | 'dispose'
    ): Promise<{ openA: boolean; openB: boolean }> => {
      const receipt = videoReceipts.get(key);
      if (receipt === undefined) throw new Error('Video recovery receipt does not own this key');
      if (op === 'dispose') {
        receipt.root.unmount();
        receipt.container.remove();
        stopSources(receipt.sources);
        videoReceipts.delete(key);
        return { openA: false, openB: false };
      }
      if (op !== 'state') {
        const index = op === 'progress-a' ? 0 : 1;
        const element = receipt.container.querySelectorAll('video')[index];
        if (element === undefined)
          throw new Error('Video recovery receipt lost its production view');
        await presentFrames(element, receipt.sources[index]);
      }
      const [elementA, elementB] = receipt.container.querySelectorAll('video');
      if (elementA === undefined || elementB === undefined)
        throw new Error('Video recovery receipt lost its production views');
      return {
        openA: receipt.readWindow(elementA) === true,
        openB: receipt.readWindow(elementB) === true
      };
    }
  );

  // Every sequence runs through the production singleton the video views use,
  // so the per-camera stall episode is exercised too. Each call uses its own
  // window keys and camera id, so receipts do not cross-talk.
  const simulate = (key: string, outcomes: AttemptOutcome[]): void => {
    streamRecoveryController.beginWindow(key, undefined, { device_id: `probe-${key}` });
    for (const outcome of outcomes) {
      streamRecoveryController.recordAttempt(key, outcome);
    }
  };

  // One camera stalls in `views` video views at once and every view recovers.
  // Production sent one success per view; one recovery must send one row.
  const simulateSharedStall = (
    key: string,
    views: number,
    ids: { agentId?: string; isEmbedded?: boolean } = {}
  ): void => {
    const context = {
      device_id: `probe-${key}`,
      ...(ids.agentId !== undefined ? { agent_id: ids.agentId } : {}),
      ...(typeof ids.isEmbedded === 'boolean' ? { is_embedded: ids.isEmbedded } : {})
    };
    const keys = Array.from({ length: views }, (_, view) => `${key}-view-${view}`);
    for (const viewKey of keys) {
      streamRecoveryController.beginWindow(viewKey, HOST_BUDGET, context);
      streamRecoveryController.recordAttempt(viewKey, 'failure');
    }
    for (const viewKey of keys) {
      streamRecoveryController.recordAttempt(viewKey, 'success');
    }
  };

  // Several views of one camera (`deviceId`) walk the given steps through the
  // production singleton: window per view key, the per-camera stall episode,
  // and the per-camera outage record. Throws on a step the controller rejects,
  // so a receipt cannot pass on a script that never ran.
  const simulateOutageScript = (key: string, deviceId: string, steps: StreamOutageStep[]): void => {
    const viewKey = (view: string): string => `${key}-${view}`;
    for (const step of steps) {
      if (step.op === 'play') {
        noteStreamProgress(deviceId);
      } else if (step.op === 'open') {
        streamRecoveryController.beginWindow(viewKey(step.view), SCRIPT_BUDGET, {
          device_id: deviceId
        });
      } else if (step.op === 'unmount') {
        streamRecoveryController.abandonWindow(viewKey(step.view));
      } else if (step.op === 'recover') {
        streamRecoveryController.recordAttempt(viewKey(step.view), 'success');
      } else {
        let attempts = 0;
        while (streamRecoveryController.hasOpenWindow(viewKey(step.view))) {
          if (attempts >= MAX_GIVE_UP_ATTEMPTS) {
            throw new Error(`[stream-recovery-probe] view ${step.view} never gave up`);
          }
          streamRecoveryController.recordAttempt(viewKey(step.view), 'failure');
          attempts++;
        }
        if (attempts === 0) {
          throw new Error(`[stream-recovery-probe] view ${step.view} had no open window`);
        }
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSimulateStreamRecovery = simulate;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSimulateStreamOutageScript = simulateOutageScript;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSimulateSharedStreamStall = simulateSharedStall;
}
